import {CSSResult, html, LitElement, PropertyValues, unsafeCSS,} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import heartFilledNormal from '@breuni-ui/bdk/design-tokens/images/icons/f-ic-16_heart.svg';
import heartNormal from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_heart.svg';
import heartFilledLarge from '@breuni-ui/bdk/design-tokens/images/icons/f-ic-24_heart.svg';
import heartLarge from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_heart.svg';
import {customElement, property, state} from 'lit/decorators.js';
import {PromotionData} from '@cube/tracking-library-api/dist/@types/events/fragments.js';
import {WishlistMutationEvent} from '../../../@types/wishlist.js';
import css from './wishlist-button.scss';
import {config} from '../../../scripts/service/config.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';

enum ButtonSize {
  normal,
  large
}

function parseButtonSize(value: string): ButtonSize {
  return value === 'large' ? ButtonSize.large : ButtonSize.normal;
}

@customElement('ents-wishlist-button')
export class WishlistButtonElement extends LitElement {
  @property({
    type: Array,
    attribute: 'initial-state',
  }) private initialState: string[] = [];

  @property({type: Object, attribute: false}) private state: Record<string, boolean> = {};

  @property({type: Number}) private syan: number = 0;

  @property({type: String, attribute: 'color-id'}) private colorId?: string = undefined;

  @property({type: String, attribute: 'article-id'}) private articleId?: string = undefined;

  @property({type: Boolean, attribute: 'show-text'}) private showText: boolean = false;

  @property({type: Boolean, attribute: 'no-color'}) private noColor: boolean = false;

  @property({type: String}) private placement?: string = undefined;

  @property({type: Object}) private promotion?: PromotionData = undefined;

  @property({type: Number, attribute: 'position'}) private position?: number = undefined;

  @property({
    type: String,
    attribute: 'size',
    converter: {fromAttribute: size => parseButtonSize(size ?? '')},
  }) private size: ButtonSize = ButtonSize.normal;

  @state()
  private animationActive: boolean = false;

  private hasJustBeenClicked: boolean = false;

  private resetAnimationTimeout: number | undefined;

  private readonly isTouchDevice: boolean = ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // use this syntax to make it work in Safari 14
  // working: static get styles() { return css`...` }
  // not working: static styles = css`...`
  static get styles(): CSSResult {
    return unsafeCSS(css);
  }

  private readonly onWishlistAddedEvent: (e: WishlistMutationEvent) => void;

  private readonly onWishlistRemovedEvent: (e: WishlistMutationEvent) => void;

  private selectedSlotNodes?: NodeListOf<Element> = undefined;

  private unselectedSlotNodes?: NodeListOf<Element> = undefined;

  attributeChangedCallback(name: string, _old: string | null, value: string | null) {
    super.attributeChangedCallback(name, _old, value);
    if (name === 'initial-state') {
      this.state = this.getInitialState();
    }
  }

  constructor() {
    super();

    this.onWishlistAddedEvent = (e: WishlistMutationEvent) => {
      const data = e.detail;
      this.state = {...this.state, ...{[data.identifier]: true}};
    };

    this.onWishlistRemovedEvent = (e: WishlistMutationEvent) => {
      const data = e.detail;
      this.state = {...this.state, ...{[data.identifier]: false}};
      this.resetAnimationImmediate();
    };

    this.state = {};
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.addEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);

    this.state = this.getInitialState();

    this.selectedSlotNodes = this.querySelectorAll('[slot="selected"]');
    this.unselectedSlotNodes = this.querySelectorAll('[slot="unselected"]');
  }

  private getInitialState() {
    const initialState = this.initialState ?? [];
    return initialState.reduce((agg, v) => ({
      ...agg,
      [v]: true,
    }), {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.removeEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);

    if (!this.isTouchDevice) {
      this.removeEventListener('mouseleave', this.resetAnimationScheduled);
    }
  }

  shouldUpdate(changedProperties: PropertyValues) {
    // Prevent updates during animation
    if (this.animationActive) {
      if (!this.hasJustBeenClicked) return false;
      this.hasJustBeenClicked = false;
    }
    return super.shouldUpdate(changedProperties);
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    this.setAttribute('selected', this.isOnWishlist() ? 'true' : 'false');
  }

  private isOnWishlist() {
    return this.state[ProductIdentifier.create(this.syan, this.colorId).id];
  }

  private getLabel() {
    if (this.isOnWishlist()) {
      return config.messageWishlistTextActive;
    }
    return config.messageWishlistTextInactive;
  }

  private handleClick(e: Event) {
    e.stopPropagation();
    if (!this.isOnWishlist()) {
      this.triggerAnimation();
    }
    const eventName = this.isOnWishlist() ? 'ents:wishlist:remove' : 'ents:wishlist:add';
    document.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        identifier: ProductIdentifier.create(this.syan, this.colorId).id,
        syan: this.syan,
        colorId: this.colorId,
        ...((this.articleId && this.articleId !== 'null') ? {articleId: this.articleId} : {}),
        tracking: {
          placement: this.placement,
          promotion: this.promotion,
          context: this.isOnWishlist() ? 'remove_heart' : 'add_heart',
          position: this.position,
        },
      },
    }));
  }

  private triggerAnimation() {
    if (this.isTouchDevice) {
      // directly schedule animation reset (touch)
      this.resetAnimationScheduled();
    } else {
      // schedule animation reset on mouse leave (non-touch)
      this.addEventListener('mouseleave', this.resetAnimationScheduled);
    }
    this.animationActive = true;
    this.hasJustBeenClicked = true;
  }

  private resetAnimationScheduled() {
    if (!this.isTouchDevice) {
      this.removeEventListener('mouseleave', this.resetAnimationScheduled);
    }
    this.resetAnimationTimeout = window.setTimeout(() => {
      this.animationActive = false;
    }, 700); // wait 700ms to be certain that the animation is finished before resetting
  }

  private resetAnimationImmediate() {
    window.clearTimeout(this.resetAnimationTimeout); // in case an animation reset has been scheduled, abort the timer
    if (!this.isTouchDevice) {
      this.removeEventListener('mouseleave', this.resetAnimationScheduled);
    }
    this.animationActive = false;
  }

  private hasSlottedContent() {
    if (this.selectedSlotNodes === undefined || this.unselectedSlotNodes === undefined) {
      return false;
    }
    return this.selectedSlotNodes.length > 0 && this.unselectedSlotNodes.length > 0;
  }


  private renderSelectedSlot() {
    this.selectedSlotNodes?.forEach(n => n.removeAttribute('style'));
    return html`
      <slot name="selected"></slot>`;
  }

  private renderUnselectedSlot() {
    this.unselectedSlotNodes?.forEach(n => n.removeAttribute('style'));
    return html`
      <slot name="unselected"></slot>`;
  }

  private renderSlottedContent() {
    return this.isOnWishlist() ? this.renderSelectedSlot() : this.renderUnselectedSlot();
  }

  private renderNativeIcon() {
    let icon: SVGElement;
    if (this.size === ButtonSize.large) {
      icon = this.isOnWishlist() ? heartFilledLarge() : heartLarge();
    } else {
      icon = this.isOnWishlist() ? heartFilledNormal() : heartNormal();
    }
    return hideIconFromAssistiveTechnology(icon);
  }

  private renderContent() {
    if (this.hasSlottedContent()) {
      return this.renderSlottedContent();
    }
    const renderLabel = () => {
      if (this.showText) {
        return html`<span class="label">${this.getLabel()}</span>`;
      }
      return '';
    };
    return html`${this.renderNativeIcon()}${renderLabel()}`;
  }

  protected render() {
    return html`
      <label
        aria-label="${config.messageButtonAriaLabel}"
        @click="${(e: Event) => e.stopPropagation()}"
        class="ents-wishlist-button ${classMap({
          selected: this.isOnWishlist(),
          styled: !this.hasSlottedContent(),
          'no-color': this.noColor,
          text: this.showText,
          pulse: this.animationActive,
          'no-description': !this.showText,
        })}">
        <input
          type="checkbox"
          aria-checked="${this.isOnWishlist()}"
          ?checked="${this.isOnWishlist()}"
          @click="${this.handleClick}"
          data-test-ents-product-on-wishlist="${this.isOnWishlist()}"
          class="sr-only"
        />
        ${this.renderContent()}
      </label>
    `;
  }
}
